import { useState } from 'react';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { useCategories } from '@/contexts/CategoriesContext';
import type { CustomCategory, CustomCategoryInput } from '@/lib/firestore-categories';

const PRESET_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308',
  '#84cc16', '#22c55e', '#10b981', '#14b8a6',
  '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6',
  '#a855f7', '#ec4899', '#f43f5e', '#6b7280',
];

interface FormState {
  name: string;
  type: 'gain' | 'expense';
  color: string;
}

const EMPTY_FORM: FormState = { name: '', type: 'expense', color: '#ef4444' };

export function CategoryManager() {
  const { customCategories, addCategory, updateCategory, deleteCategory } = useCategories();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const gainCats = customCategories.filter((c) => c.type === 'gain');
  const expenseCats = customCategories.filter((c) => c.type === 'expense');

  function openAdd() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowForm(true);
  }

  function openEdit(cat: CustomCategory) {
    setEditingId(cat.id);
    setForm({ name: cat.name, type: cat.type, color: cat.color });
    setError('');
    setShowForm(true);
  }

  function cancelForm() {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
  }

  async function handleSave() {
    if (!form.name.trim()) {
      setError('Please enter a category name.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload: CustomCategoryInput = {
        name: form.name.trim(),
        type: form.type,
        color: form.color,
      };
      if (editingId) {
        await updateCategory(editingId, payload);
      } else {
        await addCategory(payload);
      }
      cancelForm();
    } catch (e) {
      console.error(e);
      setError('Failed to save category.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    setDeletingId(id);
    try {
      await deleteCategory(id);
    } catch (e) {
      console.error(e);
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="space-y-4">
      {/* Add button */}
      {!showForm && (
        <button
          onClick={openAdd}
          className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2.5 text-sm font-semibold text-white shadow transition-all hover:from-blue-700 hover:to-purple-700 active:scale-95"
        >
          <Plus className="h-4 w-4" />
          Add Category
        </button>
      )}

      {/* Inline form */}
      {showForm && (
        <div className="rounded-xl border-2 border-blue-200 bg-blue-50/60 p-4 dark:border-blue-800 dark:bg-blue-900/20 space-y-3">
          <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">
            {editingId ? 'Edit Category' : 'New Category'}
          </h3>

          {error && (
            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          )}

          {/* Name */}
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Category name"
            className="w-full rounded-lg bg-white px-3 py-2 text-sm border border-slate-200 focus:border-blue-500 focus:outline-none dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100"
          />

          {/* Type toggle */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, type: 'expense' }))}
              className={`flex-1 rounded-lg py-2 text-xs font-semibold transition-all ${
                form.type === 'expense'
                  ? 'bg-red-500 text-white'
                  : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
              }`}
            >
              💳 Expense
            </button>
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, type: 'gain' }))}
              className={`flex-1 rounded-lg py-2 text-xs font-semibold transition-all ${
                form.type === 'gain'
                  ? 'bg-emerald-500 text-white'
                  : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
              }`}
            >
              💰 Income
            </button>
          </div>

          {/* Color picker */}
          <div>
            <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-2">Color</p>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, color }))}
                  className="relative h-7 w-7 rounded-full transition-transform hover:scale-110"
                  style={{ backgroundColor: color }}
                >
                  {form.color === color && (
                    <Check className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50 active:scale-95 transition-all"
            >
              <Check className="h-3.5 w-3.5" />
              {saving ? 'Saving…' : editingId ? 'Update' : 'Create'}
            </button>
            <button
              onClick={cancelForm}
              className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-4 py-2 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-300 active:scale-95 transition-all"
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Category lists */}
      {customCategories.length === 0 && !showForm && (
        <p className="text-sm text-slate-500 dark:text-slate-400 italic">
          No custom categories yet. Add one above to get started!
        </p>
      )}

      {expenseCats.length > 0 && (
        <CategoryList
          title="Expense Categories"
          categories={expenseCats}
          onEdit={openEdit}
          onDelete={handleDelete}
          deletingId={deletingId}
          editingId={editingId}
        />
      )}

      {gainCats.length > 0 && (
        <CategoryList
          title="Income Categories"
          categories={gainCats}
          onEdit={openEdit}
          onDelete={handleDelete}
          deletingId={deletingId}
          editingId={editingId}
        />
      )}
    </div>
  );
}

function CategoryList({
  title,
  categories,
  onEdit,
  onDelete,
  deletingId,
  editingId,
}: {
  title: string;
  categories: CustomCategory[];
  onEdit: (cat: CustomCategory) => void;
  onDelete: (id: string) => void;
  deletingId: string | null;
  editingId: string | null;
}) {
  const [confirmId, setConfirmId] = useState<string | null>(null);

  return (
    <div>
      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {title}
      </p>
      <div className="space-y-2">
        {categories.map((cat) => (
          <div
            key={cat.id}
            className={`flex items-center justify-between rounded-xl border px-3 py-2.5 transition-all ${
              editingId === cat.id
                ? 'border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20'
                : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800/60'
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <span
                className="h-4 w-4 flex-shrink-0 rounded-full"
                style={{ backgroundColor: cat.color }}
              />
              <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                {cat.name}
              </span>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0 ml-2">
              {confirmId === cat.id ? (
                <>
                  <span className="text-xs text-slate-500 dark:text-slate-400 mr-1">Delete?</span>
                  <button
                    onClick={() => {
                      setConfirmId(null);
                      onDelete(cat.id);
                    }}
                    disabled={deletingId === cat.id}
                    className="rounded-lg bg-red-100 px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 transition-colors"
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => setConfirmId(null)}
                    className="rounded-lg bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 transition-colors"
                  >
                    No
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => onEdit(cat)}
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-blue-600 dark:hover:bg-slate-700 dark:hover:text-blue-400 transition-colors"
                    title="Edit"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setConfirmId(cat.id)}
                    disabled={deletingId === cat.id}
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
